import * as React from 'react';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './collapsible';
import { useFeatureFlag } from './feature-flags';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from './sidebar';

/**
 * The application navigation stack shared by every Artic SPA on the modern
 * shell — Neo and Dashboards today.
 *
 * The apps sit on different origins, so the same conceptual destination is an
 * in-app route in one and an absolute URL in another: Neo's Quality Inbox is
 * `to: '/inbox'` in Neo and `externalUrl: 'https://quality.…/inbox'` in
 * Dashboards. Only the *config* differs — the rendering, flag semantics and
 * collapsed-rail popovers live here so the two navs cannot drift.
 *
 * Labels arrive already translated: `@artic/ui` has no i18n dependency, so the
 * host app resolves every string (including {@link AppNavProps.unavailableLabel})
 * through its own `@artic/i18n` instance.
 */

export interface NavItem {
  title: string;
  /** In-app route, rendered through the host router's `LinkComponent`. */
  to?: string;
  /** Hub URL path, resolved against {@link AppNavProps.hubUrl}. */
  hubPath?: string;
  /** Fully-qualified external URL (rendered as-is, e.g. drawings.artic.works) */
  externalUrl?: string;
  hide?: boolean;
  disabled?: boolean;
  /** When true, only the exact path matches as active. */
  exact?: boolean;
  /**
   * Feature flag key. When set, the item routes through `FlaggedNavLink`: the
   * link is shown actively when the flag is on, faded when the flag is off and
   * a real fallback page exists (`hubPath`, or `offLink` if provided), and
   * disabled otherwise — including for an `externalUrl`, which is the flagged
   * target rather than a fallback.
   */
  flag?: string;
  /** Route used when `flag` is off and a fallback target is desired. */
  offLink?: string;
}

export interface NavSubheader {
  kind: 'subheader';
  label: string;
  items: NavItem[];
  hide?: boolean;
}

export interface NavGroup {
  title: string;
  icon: React.ElementType;
  items: (NavItem | NavSubheader)[];
  hide?: boolean;
  /** Open the group on first paint even when no child route is active. */
  defaultExpanded?: boolean;
}

/**
 * The host router's `Link`, narrowed to what the nav needs. Each app passes a
 * thin wrapper around its own router's component so this package stays free of
 * a router dependency.
 *
 * The wrapper MUST forward any props it doesn't consume onto the rendered
 * anchor: the nav mounts links through `asChild`, so Radix's Slot injects
 * `data-active`, `data-sidebar` and event handlers into them. A wrapper that
 * destructures only `to`/`className`/`children` silently drops the active
 * state.
 */
export type NavLinkComponent = React.ComponentType<{
  to: string;
  /** Explicitly `| undefined` — the nav omits it on unstyled rows and the SPAs
   * build under `exactOptionalPropertyTypes`. */
  className?: string | undefined;
  children: React.ReactNode;
}>;

interface NavRenderContextValue {
  LinkComponent: NavLinkComponent;
  hubUrl: string;
  currentPath: string;
  unavailableLabel: string;
}

const NavRenderContext = React.createContext<NavRenderContextValue | null>(null);

function useNavRender(): NavRenderContextValue {
  const ctx = React.useContext(NavRenderContext);
  if (!ctx) throw new Error('AppNav internals must be rendered inside <AppNav>');
  return ctx;
}

export function isSubheader(entry: NavItem | NavSubheader): entry is NavSubheader {
  return 'kind' in entry && entry.kind === 'subheader';
}

function visibleEntries(items: (NavItem | NavSubheader)[]): (NavItem | NavSubheader)[] {
  const result: (NavItem | NavSubheader)[] = [];
  for (const entry of items) {
    if (entry.hide) continue;
    if (!isSubheader(entry)) {
      result.push(entry);
      continue;
    }
    const visibleSubItems = entry.items.filter((i) => !i.hide);
    if (visibleSubItems.length === 0) continue;
    result.push({ ...entry, items: visibleSubItems });
  }
  return result;
}

function SmartLink({
  to,
  children,
  className,
}: {
  to: string;
  children: React.ReactNode;
  className?: string;
}) {
  const { LinkComponent } = useNavRender();
  if (to.startsWith('http') || to.startsWith('//')) {
    return (
      <a href={to} className={className}>
        {children}
      </a>
    );
  }
  return (
    <LinkComponent to={to} className={className}>
      {children}
    </LinkComponent>
  );
}

function resolveFlagLink(hubUrl: string, candidate: string | undefined): string | undefined {
  if (!candidate) return undefined;
  if (candidate.startsWith('http') || candidate.startsWith('/')) return candidate;
  return `${hubUrl}${candidate}`;
}

function isItemActive(item: NavItem, currentPath: string): boolean {
  if (!item.to) return false;
  if (item.exact) return currentPath === item.to;
  return currentPath === item.to || currentPath.startsWith(`${item.to}/`);
}

function NavLink({ item, isSub }: { item: NavItem; isSub?: boolean }) {
  const subProps = isSub !== undefined ? { isSub } : {};
  if (item.flag) {
    return <FlaggedNavLink item={item} {...subProps} />;
  }
  return <PlainNavLink item={item} {...subProps} />;
}

function FlaggedNavLink({ item, isSub }: { item: NavItem; isSub?: boolean }) {
  const { hubUrl } = useNavRender();
  // Non-null asserted: NavLink only delegates here when item.flag is set.
  const enabled = useFeatureFlag(item.flag!);
  const subProps = isSub !== undefined ? { isSub } : {};

  if (enabled) {
    return <PlainNavLink item={item} {...subProps} />;
  }

  // Flag off: prefer a Hub fallback (hubPath) so the link still reaches a real
  // page. `resolveFlagLink` can't turn a leading-slash path into a Hub URL (it
  // would render an in-app link to a dead route), so build the absolute Hub
  // href the same way PlainNavLink does.
  //
  // An `externalUrl` is deliberately NOT treated as a fallback: it is the
  // flagged target itself (the Drawing Control app, the Dashboards app), not a
  // legacy page that exists either way, so a flag-off item is disabled rather
  // than faded. That also matches the collapsed sidebar, where
  // `FlagPopoverItem` has always rendered a flag-off `externalUrl` as a
  // `DisabledPopoverRow`.
  if (item.hubPath) {
    return <FadedNavLink item={item} href={`${hubUrl}${item.hubPath}`} {...subProps} />;
  }

  if (item.offLink) {
    const offLink = resolveFlagLink(hubUrl, item.offLink) ?? '';
    return <FadedNavLink item={item} href={offLink} {...subProps} />;
  }

  // Flag off, no fallback: render through the same disabled path as item.disabled.
  return <PlainNavLink item={{ ...item, disabled: true }} {...subProps} />;
}

function FadedNavLink({ item, href, isSub }: { item: NavItem; href: string; isSub?: boolean }) {
  const inner = (
    <SmartLink to={href}>
      <span>{item.title}</span>
    </SmartLink>
  );
  if (isSub) {
    return (
      <SidebarMenuSubItem>
        <SidebarMenuSubButton asChild className="text-sm opacity-60">
          {inner}
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    );
  }
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={item.title} className="opacity-60">
        {inner}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function PlainNavLink({ item, isSub }: { item: NavItem; isSub?: boolean }) {
  const { LinkComponent, hubUrl, currentPath } = useNavRender();

  if (item.disabled) {
    if (isSub) {
      return (
        <SidebarMenuSubItem>
          <SidebarMenuSubButton asChild aria-disabled className="cursor-not-allowed opacity-50">
            <span>
              <span>{item.title}</span>
            </span>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      );
    }
    return (
      <SidebarMenuItem>
        <SidebarMenuButton disabled aria-disabled tooltip={item.title}>
          <span>{item.title}</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  const isActive = isItemActive(item, currentPath);

  if (item.to) {
    if (isSub) {
      return (
        <SidebarMenuSubItem>
          <SidebarMenuSubButton asChild isActive={isActive}>
            <LinkComponent to={item.to}>
              <span>{item.title}</span>
            </LinkComponent>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      );
    }
    return (
      <SidebarMenuItem>
        <SidebarMenuButton asChild tooltip={item.title} isActive={isActive}>
          <LinkComponent to={item.to}>
            <span>{item.title}</span>
          </LinkComponent>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  const href = item.externalUrl ?? `${hubUrl}${item.hubPath ?? ''}`;
  if (isSub) {
    return (
      <SidebarMenuSubItem>
        <SidebarMenuSubButton asChild>
          <a href={href}>
            <span>{item.title}</span>
          </a>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    );
  }
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={item.title}>
        <a href={href}>
          <span>{item.title}</span>
        </a>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function ExpandedSubheaderRow({ label }: { label: string }) {
  return (
    <li className="text-sidebar-foreground/60 px-2 pt-2 pb-0.5 text-[10px] font-medium tracking-wider uppercase">
      {label}
    </li>
  );
}

function DisabledPopoverRow({ item }: { item: NavItem }) {
  const { unavailableLabel } = useNavRender();
  return (
    <li
      aria-disabled
      className="text-sidebar-foreground/70 flex cursor-not-allowed items-center gap-2 rounded-md px-2 py-1.5 text-sm opacity-50"
      title={unavailableLabel}
    >
      <span>{item.title}</span>
    </li>
  );
}

function FlagPopoverItem({ item }: { item: NavItem }) {
  const { hubUrl } = useNavRender();
  // Hook must always run; non-null assertion is safe because the caller only
  // delegates to this component when `item.flag` is set.
  const enabled = useFeatureFlag(item.flag!);
  if (enabled) {
    return <PopoverItemContent item={item} />;
  }

  // Same fallback ladder as FlaggedNavLink, so an item does not change
  // behaviour when the rail is collapsed: a Hub fallback still reaches a real
  // page, and only an item with nowhere to go is disabled.
  if (item.hubPath) {
    return <FadedPopoverRow item={item} href={`${hubUrl}${item.hubPath}`} />;
  }

  const offLink = item.offLink ? resolveFlagLink(hubUrl, item.offLink) : undefined;
  if (offLink) {
    return <FadedPopoverRow item={item} href={offLink} />;
  }

  return <DisabledPopoverRow item={item} />;
}

function FadedPopoverRow({ item, href }: { item: NavItem; href: string }) {
  return (
    <li>
      <SmartLink
        to={href}
        className="hover:bg-sidebar-accent hover:text-sidebar-accent-foreground flex items-center gap-2 rounded-md px-2 py-1.5 text-sm opacity-60"
      >
        <span>{item.title}</span>
      </SmartLink>
    </li>
  );
}

function PopoverItem({ item }: { item: NavItem }) {
  if (item.flag) {
    return <FlagPopoverItem item={item} />;
  }
  return <PopoverItemContent item={item} />;
}

function PopoverItemContent({ item }: { item: NavItem }) {
  const { LinkComponent, hubUrl, currentPath } = useNavRender();

  if (item.disabled) {
    return <DisabledPopoverRow item={item} />;
  }

  const isActive = isItemActive(item, currentPath);

  const baseClass =
    'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground';
  const activeClass = isActive
    ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
    : '';

  if (item.to) {
    return (
      <li>
        <LinkComponent to={item.to} className={`${baseClass} ${activeClass}`.trim()}>
          <span>{item.title}</span>
        </LinkComponent>
      </li>
    );
  }
  const href = item.externalUrl ?? `${hubUrl}${item.hubPath ?? ''}`;
  return (
    <li>
      <a href={href} className={baseClass}>
        <span>{item.title}</span>
      </a>
    </li>
  );
}

function PopoverSubheaderRow({ label }: { label: string }) {
  return (
    <li className="text-sidebar-foreground/60 px-2 pt-2 pb-0.5 text-[10px] font-medium tracking-wider uppercase">
      {label}
    </li>
  );
}

function NavGroupRender({ group }: { group: NavGroup }) {
  const { currentPath } = useNavRender();
  const { state } = useSidebar();
  const GroupIcon = group.icon;
  const visible = visibleEntries(group.items);
  const [popoverOpen, setPopoverOpen] = React.useState(false);
  const closeTimerRef = React.useRef<number | null>(null);

  React.useEffect(
    () => () => {
      if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current);
    },
    [],
  );

  if (visible.length === 0) return null;

  if (state === 'collapsed') {
    const cancelClose = () => {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
    const scheduleClose = () => {
      cancelClose();
      closeTimerRef.current = window.setTimeout(() => setPopoverOpen(false), 150);
    };
    const openNow = () => {
      cancelClose();
      setPopoverOpen(true);
    };
    return (
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <SidebarMenuItem onMouseEnter={openNow} onMouseLeave={scheduleClose}>
          <PopoverTrigger asChild>
            {/* "dialog", not "menu": the popover body is a plain list of links,
                with no role="menu"/"menuitem" semantics to back that promise. */}
            <SidebarMenuButton aria-haspopup="dialog" aria-expanded={popoverOpen}>
              <GroupIcon aria-hidden={true} />
              <span>{group.title}</span>
            </SidebarMenuButton>
          </PopoverTrigger>
        </SidebarMenuItem>
        <PopoverContent
          side="right"
          align="start"
          sideOffset={12}
          className="w-60 p-2"
          onMouseEnter={openNow}
          onMouseLeave={scheduleClose}
          onOpenAutoFocus={(event) => {
            // Keep focus on the trigger when the popover opens via hover —
            // tabbing into the content is still possible. Stops the page
            // from jumping when the user only mouses over.
            event.preventDefault();
          }}
        >
          <p className="text-sidebar-foreground/60 px-2 pb-1 text-[10px] font-medium tracking-wider uppercase">
            {group.title}
          </p>
          <ul className="flex flex-col gap-0.5">
            {visible.map((entry) => {
              if (isSubheader(entry)) {
                return (
                  <React.Fragment key={`sh-${entry.label}`}>
                    <PopoverSubheaderRow label={entry.label} />
                    {entry.items.map((sub) => (
                      <PopoverItem key={sub.title} item={sub} />
                    ))}
                  </React.Fragment>
                );
              }
              return <PopoverItem key={entry.title} item={entry} />;
            })}
          </ul>
        </PopoverContent>
      </Popover>
    );
  }

  const flatItems = visible.flatMap((entry) => (isSubheader(entry) ? entry.items : [entry]));
  const hasActiveChild = flatItems.some(
    (item) => item.to && (currentPath === item.to || currentPath.startsWith(`${item.to}/`)),
  );

  return (
    <Collapsible
      defaultOpen={hasActiveChild || (group.defaultExpanded ?? false)}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton>
            <GroupIcon aria-hidden={true} />
            <span>{group.title}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {visible.map((entry) => {
              if (isSubheader(entry)) {
                return (
                  <React.Fragment key={`sh-${entry.label}`}>
                    <ExpandedSubheaderRow label={entry.label} />
                    {entry.items.map((sub) => (
                      <NavLink key={sub.title} item={sub} isSub />
                    ))}
                  </React.Fragment>
                );
              }
              return <NavLink key={entry.title} item={entry} isSub />;
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

export interface AppNavProps {
  groups: NavGroup[];
  /** The host router's `Link`, used for every in-app `to` destination. */
  LinkComponent: NavLinkComponent;
  /** The current in-app pathname, used for active-item matching. */
  currentPath: string;
  /** Absolute Hub origin that `hubPath` items resolve against. */
  hubUrl: string;
  /** Translated label for the sidebar group, e.g. "Navigation". */
  label: string;
  /** Translated tooltip on a disabled row, e.g. "Not available for your account". */
  unavailableLabel: string;
}

export function AppNav({
  groups,
  LinkComponent,
  currentPath,
  hubUrl,
  label,
  unavailableLabel,
}: AppNavProps) {
  const renderContext = React.useMemo(
    () => ({ LinkComponent, hubUrl, currentPath, unavailableLabel }),
    [LinkComponent, hubUrl, currentPath, unavailableLabel],
  );

  return (
    <NavRenderContext value={renderContext}>
      <SidebarGroup>
        <SidebarGroupLabel>{label}</SidebarGroupLabel>
        <SidebarMenu>
          {groups.map((group) => {
            if (group.hide) return null;
            return <NavGroupRender key={group.title} group={group} />;
          })}
        </SidebarMenu>
      </SidebarGroup>
    </NavRenderContext>
  );
}
